/**
 * TonalFilter V2 - Tonal adjustments using ColorMatrixFilter
 *
 * Implements highlights, shadows, whites, and blacks adjustments using
 * color matrix operations. While not as sophisticated as custom GLSL shaders,
 * this provides good approximations that work reliably with PixiJS 8.
 *
 * Algorithm approach:
 * - Shadows: Lift the black point (additive offset for dark tones)
 * - Highlights: Compress the upper range (multiplicative scaling)
 * - Whites: Shift the white point (additive offset for all tones)
 * - Blacks: Shift the black point (additive offset, opposite of whites)
 */

import { ColorMatrixFilter } from 'pixi.js'

export class TonalFilterV2 extends ColorMatrixFilter {
  private _highlights = 0
  private _shadows = 0
  private _whites = 0
  private _blacks = 0

  constructor() {
    super()
    this._updateMatrix()
  }

  setHighlights(value: number): void {
    this._highlights = value
    this._updateMatrix()
  }

  setShadows(value: number): void {
    this._shadows = value
    this._updateMatrix()
  }

  setWhites(value: number): void {
    this._whites = value
    this._updateMatrix()
  }

  setBlacks(value: number): void {
    this._blacks = value
    this._updateMatrix()
  }

  private _updateMatrix(): void {
    // Normalize values from -100..100 to usable ranges
    const highlights = this._highlights / 100  // -1.0 to +1.0
    const shadows = this._shadows / 100        // -1.0 to +1.0
    const whites = this._whites / 100          // -1.0 to +1.0
    const blacks = this._blacks / 100          // -1.0 to +1.0

    // Calculate combined effects
    // Shadows: positive values lift dark areas (additive)
    // Highlights: negative values compress bright areas (multiplicative)
    // Whites: positive values shift everything brighter
    // Blacks: negative values shift everything darker

    // Multiplicative component (affects relative brightness)
    // Highlights: negative compresses (darkens highlights), positive expands
    const highlightScale = 1.0 + (highlights * 0.15)

    // Additive component (affects absolute brightness)
    // Shadows: positive lifts dark areas (brightens shadows)
    // Whites: positive shifts everything brighter
    // Blacks: negative shifts everything darker
    const shadowLift = shadows * 0.12        // Positive brightens shadows
    const whiteShift = whites * 0.08         // Positive shifts up
    const blackShift = blacks * 0.08         // Negative shifts down

    // Combined offset: all additive
    const totalOffset = shadowLift + whiteShift + blackShift

    // Build the color matrix
    // Format: [R, G, B, A, offset] for each channel
    // We apply the same transformation to R, G, and B to preserve color
    this.matrix = [
      // Red channel: scale + offset
      highlightScale, 0, 0, 0, totalOffset,
      // Green channel: scale + offset
      0, highlightScale, 0, 0, totalOffset,
      // Blue channel: scale + offset
      0, 0, highlightScale, 0, totalOffset,
      // Alpha channel: unchanged
      0, 0, 0, 1, 0,
    ] as any
  }

  reset(): void {
    this._highlights = 0
    this._shadows = 0
    this._whites = 0
    this._blacks = 0
    this._updateMatrix()
  }

  getHighlights(): number {
    return this._highlights
  }

  getShadows(): number {
    return this._shadows
  }

  getWhites(): number {
    return this._whites
  }

  getBlacks(): number {
    return this._blacks
  }
}
